import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  memoryOpLabel,
  memoryOpPreview,
  splitMemoryEntries,
  type MemoryProposeResult,
} from "@/lib/customer-memory";
import { cn } from "@/lib/utils";

type CustomerMemoryIntakeProps = {
  summary: string | null | undefined;
  /** 确认写入成功后回写父级 */
  onSummaryChange: (next: string | null) => void;
  propose: (facts: string) => Promise<MemoryProposeResult>;
  apply: (summary: string) => Promise<string | null>;
  /** workbench：shadcn 卡片；sidebar：紧凑锌色 */
  variant?: "workbench" | "sidebar";
};

/**
 * 客户记忆 AI 录入：描述事实 → 提案确认 → 落库。
 * 工作台与企微侧边栏共用。
 */
export function CustomerMemoryIntake({
  summary,
  onSummaryChange,
  propose,
  apply,
  variant = "workbench",
}: CustomerMemoryIntakeProps) {
  const [facts, setFacts] = useState("");
  const [proposing, setProposing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [draft, setDraft] = useState<MemoryProposeResult | null>(null);
  const entries = splitMemoryEntries(summary);
  const isSidebar = variant === "sidebar";

  async function onPropose() {
    const trimmed = facts.trim();
    if (!trimmed || proposing) return;
    setProposing(true);
    try {
      const result = await propose(trimmed);
      setDraft(result);
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : "无法识别记忆");
    } finally {
      setProposing(false);
    }
  }

  async function onConfirm() {
    if (!draft || confirming) return;
    setConfirming(true);
    try {
      const next = await apply(draft.proposed_summary ?? "");
      onSummaryChange(next);
      setFacts("");
      setDraft(null);
      toast.success("已更新客户记忆");
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : "保存失败");
    } finally {
      setConfirming(false);
    }
  }

  const proposedEntries = splitMemoryEntries(draft?.proposed_summary);

  return (
    <div
      className={cn(
        isSidebar
          ? "rounded-xl border border-zinc-200 bg-white px-3 py-2.5"
          : "rounded-md border border-border p-3",
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className={cn("font-medium", isSidebar ? "text-[13px]" : "text-sm")}>客户记忆</p>
        {isSidebar ? (
          <span className="text-[11px] text-zinc-500">确认后全员可见</span>
        ) : null}
      </div>

      {entries.length > 0 ? (
        <ul
          className={cn(
            "mt-2 list-disc space-y-1 pl-4",
            isSidebar ? "text-[13px] text-zinc-700" : "text-sm text-foreground",
          )}
        >
          {entries.map((entry) => (
            <li key={entry} className="whitespace-pre-wrap">
              {entry}
            </li>
          ))}
        </ul>
      ) : (
        <p
          className={cn(
            "mt-1",
            isSidebar ? "text-[12px] text-zinc-500" : "text-xs text-muted-foreground",
          )}
        >
          暂无客户记忆
        </p>
      )}

      <div className="mt-3 space-y-2">
        <Label
          htmlFor={isSidebar ? "sidebar-memory-facts" : "workbench-memory-facts"}
          className={cn(isSidebar ? "text-[12px] text-zinc-600" : "text-xs")}
        >
          补充事实
        </Label>
        <Textarea
          id={isSidebar ? "sidebar-memory-facts" : "workbench-memory-facts"}
          value={facts}
          onChange={(e) => setFacts(e.target.value)}
          placeholder="用自然语言描述这位客户的关键事实，例如：偏好周末回电、决策人是张总"
          rows={3}
          className={cn(
            isSidebar &&
              "min-h-20 resize-y border-zinc-200 bg-zinc-50 text-[13px] text-zinc-700 focus-visible:border-zinc-400",
          )}
        />
        <Button
          type="button"
          size={isSidebar ? "default" : "sm"}
          disabled={!facts.trim() || proposing}
          onClick={() => void onPropose()}
          className={cn(
            isSidebar &&
              "h-9 w-full rounded-lg bg-zinc-900 text-[13px] font-semibold text-white hover:bg-zinc-800",
          )}
        >
          {proposing ? "识别中…" : "识别记忆"}
        </Button>
      </div>

      <ConfirmDialog
        open={draft != null}
        onOpenChange={(open) => {
          if (!open && !confirming) setDraft(null);
        }}
        title="确认更新客户记忆"
        confirmLabel="写入记忆"
        cancelLabel="取消"
        confirming={confirming}
        onConfirm={() => void onConfirm()}
        description={
          draft ? (
            <div className="space-y-3 text-left text-sm text-foreground">
              {draft.ops.length > 0 ? (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">拟变更</p>
                  <ul className="space-y-1">
                    {draft.ops.map((op, index) => (
                      <li
                        key={`${op.action}-${index}`}
                        className="rounded-md border border-border bg-muted/30 px-2 py-1.5 text-xs"
                      >
                        <span className="font-medium">{memoryOpLabel(op)}</span>
                        <span className="mt-0.5 block whitespace-pre-wrap text-muted-foreground">
                          {memoryOpPreview(op)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">无条目级变更，将按下方全文写入。</p>
              )}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">写入后</p>
                {proposedEntries.length > 0 ? (
                  <ul className="list-disc space-y-1 pl-4 text-xs">
                    {proposedEntries.map((entry) => (
                      <li key={entry} className="whitespace-pre-wrap">
                        {entry}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">（清空记忆）</p>
                )}
              </div>
            </div>
          ) : null
        }
      />
    </div>
  );
}
